import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Trash2 } from 'lucide-react';
import type { AjustesPrivacidad, ClienteParaBorrar, ResultadoBorrado } from '@shared/privacidad';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';

/**
 * Sprint 167 — Configuración → Privacidad de clientes. Sólo el dueño (sección `privacidad`).
 *
 * Dos cosas, las dos que pide la Ley de Protección de Datos Personales y que el sistema no tenía:
 *  1. Quién responde por los datos de los clientes: lo que dice el aviso de privacidad del enlace.
 *  2. Borrar a un cliente que lo pide ("ya no quiero que tengan mis datos"). Irreversible.
 *
 * El plazo de 12 meses no se configura aquí: se cumple solo y la pantalla sólo lo dice.
 */
export function PrivacidadForm() {
  return (
    <div className="space-y-6">
      <DatosDelAviso />
      <div className="border-t border-slate-200 pt-6">
        <BorrarCliente />
      </div>
    </div>
  );
}

function DatosDelAviso() {
  const [ajustes, setAjustes] = useState<AjustesPrivacidad | null>(null);
  const [form, setForm] = useState({ responsable: '', domicilio: '', contacto: '' });
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ ok: boolean; texto: string } | null>(null);

  useEffect(() => {
    void window.api.privacidad.ajustes().then((r) => {
      if (r.ok) {
        setAjustes(r.data);
        setForm({ responsable: r.data.responsable, domicilio: r.data.domicilio, contacto: r.data.contacto });
      }
    });
  }, []);

  if (!ajustes) return null;

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    const r = await window.api.privacidad.guardar(form);
    setGuardando(false);
    if (r.ok) {
      setAjustes(r.data);
      setMensaje({ ok: true, texto: 'Guardado. El aviso del enlace ya lo dice así.' });
    } else setMensaje({ ok: false, texto: traducirErrorBackend(r.error, r.codigo) });
  };

  const campo = (clave: keyof typeof form, etiqueta: string, ayuda: string, max: number) => (
    <label className="block">
      <span className="text-base font-semibold text-slate-900">{etiqueta}</span>
      <input
        className="input mt-1"
        value={form[clave]}
        maxLength={max}
        placeholder={ajustes.sugeridos[clave] || ''}
        onChange={(e) => setForm({ ...form, [clave]: e.target.value })}
      />
      <span className="mt-1 block text-sm text-slate-600">{ayuda}</span>
    </label>
  );

  return (
    <div className="card max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
        <h3 className="text-lg font-bold">Aviso de privacidad del enlace</h3>
      </div>
      <p className="text-base text-slate-700">
        Quien pide por el enlace deja su nombre, su WhatsApp y su dirección. La ley pide decirle{' '}
        <strong>quién cuida esos datos y a quién pedirle que los borre</strong>. Esto es lo que lee en el aviso.
        Si dejas un campo vacío, se usa el dato de tu negocio que aparece en gris.
      </p>
      {campo('responsable', 'Nombre legal del negocio o del dueño', 'Como aparece ante Hacienda (persona o empresa).', 120)}
      {campo('domicilio', 'Domicilio', 'El del negocio.', 300)}
      {campo('contacto', 'Dónde te piden borrar sus datos', 'Un WhatsApp o un correo que sí revises.', 160)}
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        Los datos de un cliente <strong>se borran solos</strong> cuando pasan {ajustes.mesesConservar} meses sin que pida.
        Sus ventas se quedan en tus reportes, sin su nombre, teléfono ni dirección.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary min-h-[44px]" disabled={guardando} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <a
          href="#/privacidad"
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-[44px] items-center font-semibold text-primary underline"
        >
          Ver el aviso como lo ve el cliente
        </a>
      </div>
      {mensaje && (
        <p
          className={`rounded-lg px-3 py-2 text-sm ${mensaje.ok ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-800'}`}
          role={mensaje.ok ? 'status' : 'alert'}
        >
          {mensaje.texto}
        </p>
      )}
    </div>
  );
}

function BorrarCliente() {
  const [telefono, setTelefono] = useState('');
  const [buscado, setBuscado] = useState<ClienteParaBorrar | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [mensaje, setMensaje] = useState<{ ok: boolean; texto: string } | null>(null);
  const enCurso = useRef(false);
  const stepUp = useStepUp<ResultadoBorrado>();

  const digitos = telefono.replace(/\D/g, '');

  const buscar = async () => {
    setMensaje(null);
    setConfirmando(false);
    setBuscado(null);
    const r = await window.api.privacidad.buscarCliente({ telefono });
    if (r.ok) setBuscado(r.data);
    else setMensaje({ ok: false, texto: traducirErrorBackend(r.error, r.codigo) });
  };

  const borrar = async () => {
    // Freno antes del await: un doble toque no puede mandar dos borrados.
    if (enCurso.current) return;
    enCurso.current = true;
    setTrabajando(true);
    const r = await stepUp.ejecutar(() => window.api.privacidad.borrarCliente({ telefono }));
    enCurso.current = false;
    setTrabajando(false);
    setConfirmando(false);
    if (r.ok) {
      setBuscado(null);
      setTelefono('');
      setMensaje({
        ok: true,
        texto:
          r.data.pedidos > 0
            ? `Listo. Se borraron sus datos de ${r.data.pedidos} ${r.data.pedidos === 1 ? 'pedido' : 'pedidos'}. Sus ventas siguen en tus reportes, sin su nombre.`
            : 'Listo. Se borraron sus datos.',
      });
    } else if (r.codigo !== 'STEP_UP_REQUIRED' && r.codigo !== 'CANCELADO') {
      setMensaje({ ok: false, texto: traducirErrorBackend(r.error, r.codigo) });
    }
  };

  return (
    <div className="card max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Trash2 className="h-5 w-5 text-red-700" aria-hidden />
        <h3 className="text-lg font-bold">Un cliente pidió que borres sus datos</h3>
      </div>
      <p className="text-base text-slate-700">
        Escribe el teléfono con el que pide. Se borran su nombre, su teléfono, su dirección y su ubicación de todos sus
        pedidos. <strong>No se puede deshacer.</strong>
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          className="input max-w-xs"
          type="tel"
          inputMode="tel"
          maxLength={20}
          placeholder="993 123 4567"
          value={telefono}
          onChange={(e) => {
            setTelefono(e.target.value);
            setBuscado(null);
            setConfirmando(false);
          }}
          aria-label="Teléfono del cliente"
        />
        <button type="button" className="btn-secondary min-h-[44px]" disabled={digitos.length < 10} onClick={() => void buscar()}>
          Buscar
        </button>
      </div>

      {buscado && !buscado.encontrado && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-base text-slate-700">No hay ningún cliente con ese teléfono.</p>
      )}

      {buscado?.encontrado && (
        <div className="space-y-3 rounded-lg border border-slate-200 p-3">
          <p className="text-base text-slate-900">
            <strong>{buscado.nombre ?? 'Sin nombre'}</strong> · {buscado.pedidos} {buscado.pedidos === 1 ? 'pedido' : 'pedidos'}
          </p>
          {buscado.pendientes.length > 0 ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-base text-amber-900" role="alert">
              Todavía no se puede: {buscado.pendientes.length === 1 ? 'el pedido' : 'los pedidos'}{' '}
              {buscado.pendientes.map((n) => `#${n}`).join(', ')} {buscado.pendientes.length === 1 ? 'está' : 'están'} en curso o
              sin pagar. Termínalos y vuelve a buscarlo.
            </p>
          ) : !confirmando ? (
            <button
              type="button"
              className="min-h-[48px] rounded-lg bg-red-700 px-4 font-bold text-white"
              onClick={() => setConfirmando(true)}
            >
              Borrar sus datos
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-base font-semibold text-red-800">
                ¿Seguro? Ya no vas a poder ver su nombre, su teléfono ni su dirección en ningún pedido.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="min-h-[48px] rounded-lg bg-red-700 px-4 font-bold text-white disabled:opacity-50"
                  disabled={trabajando}
                  onClick={() => void borrar()}
                >
                  {trabajando ? 'Borrando…' : 'Sí, borrar sus datos'}
                </button>
                <button type="button" className="btn-secondary min-h-[48px]" disabled={trabajando} onClick={() => setConfirmando(false)}>
                  No, dejarlos
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mensaje && (
        <p
          className={`rounded-lg px-3 py-2 text-base ${mensaje.ok ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-800'}`}
          role={mensaje.ok ? 'status' : 'alert'}
        >
          {mensaje.texto}
        </p>
      )}
      <StepUpModal abierto={stepUp.modalAbierto} onConfirmado={stepUp.onConfirmado} onCancelar={stepUp.onCancelar} />
    </div>
  );
}
